// MARK://dhk8/pos/pages/OfflineQueuePage
// "Conexión" — the real /offline/cola screen.
//
// Two things a cashier needs after a network blip:
//   1. Estado de conexión — am I online (nube directa / relay LAN) or sin conexión,
//      and when did we last sync? Read from `lib/clients/offline.ts` which derives
//      the mode from `api-origin.ts` + navigator.onLine.
//   2. Cola de operaciones pendientes — the mutations (comandas, pagos, cierres)
//      waiting to be replayed, each with a kind, age, and per-item status
//      (pendiente / reintentando / error con mensaje). Actions: "Reintentar todo"
//      and "Vaciar registro local" (flush, behind a confirm sheet).
//
// Under PREVIEW_STUB the client returns SEED_OFFLINE_QUEUE; otherwise the queue is
// empty (the real outbox doesn't expose a status-rich list yet — see offline.ts).
//
// Shell stays token-driven (--pos-*), works in dark + light, no Tailwind, no emoji.
// Modal confirm reuses features/pos/BottomSheet (portals to body).

import { useEffect, useState } from 'react'
import { RefreshCw, Trash2, Cloud, Network, CloudOff, ClipboardList, CreditCard, ReceiptText, Circle } from 'lucide-react'
import BottomSheet from '../features/pos/BottomSheet.js'
import {
  getConnState,
  getQueue,
  retryAll,
  flushQueue,
  type ConnState,
  type ConnMode,
  type QueueItem,
  type QueueItemKind,
  type QueueItemStatus,
} from '../lib/clients/offline.js'
import { t } from '../i18n/es-MX.js'

// ── helpers ────────────────────────────────────────────────────────────────

function statusLabel(mode: ConnMode): string {
  return mode === 'lan-relay'
    ? t('conexion.status.lan')
    : mode === 'offline'
      ? t('conexion.status.offline')
      : t('conexion.status.cloud')
}

function StatusIcon({ mode }: { mode: ConnMode }) {
  if (mode === 'offline') return <CloudOff size={18} aria-hidden />
  if (mode === 'lan-relay') return <Network size={18} aria-hidden />
  return <Cloud size={18} aria-hidden />
}

function formatLastSync(iso: string | null): string {
  if (!iso) return t('conexion.lastSyncNever')
  const d = new Date(iso)
  const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  return sameDay ? time : `${d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })} ${time}`
}

function formatAge(queuedAt: number): string {
  const mins = Math.max(0, Math.round((Date.now() - queuedAt) / 60_000))
  if (mins < 1) return t('conexion.ageJustNow')
  if (mins < 60) return (mins === 1 ? t('conexion.ageMin.one') : t('conexion.ageMin.other')).replace('{n}', String(mins))
  const hrs = Math.round(mins / 60)
  return (hrs === 1 ? t('conexion.ageHr.one') : t('conexion.ageHr.other')).replace('{n}', String(hrs))
}

function kindLabel(k: QueueItemKind): string {
  return t(`conexion.kind.${k}` as 'conexion.kind.comanda')
}

function KindIcon({ kind }: { kind: QueueItemKind }) {
  if (kind === 'comanda') return <ClipboardList size={16} aria-hidden />
  if (kind === 'pago') return <CreditCard size={16} aria-hidden />
  if (kind === 'cierre') return <ReceiptText size={16} aria-hidden />
  return <Circle size={16} aria-hidden />
}

function statusItemLabel(s: QueueItemStatus): string {
  return t(`conexion.itemStatus.${s}` as 'conexion.itemStatus.pendiente')
}

function attemptsLabel(n: number): string {
  if (n <= 0) return ''
  return (n === 1 ? t('conexion.attempts.one') : t('conexion.attempts.other')).replace('{n}', String(n))
}

// ── component ──────────────────────────────────────────────────────────────

export default function OfflineQueuePage() {
  const [conn, setConn] = useState<ConnState>(() => getConnState())
  const [items, setItems] = useState<QueueItem[]>([])
  const [busy, setBusy] = useState<'idle' | 'retrying' | 'flushing'>('idle')
  const [confirmFlush, setConfirmFlush] = useState(false)
  const [flushNote, setFlushNote] = useState<number | null>(null)

  async function refresh() {
    setConn(getConnState())
    setItems(await getQueue())
  }

  useEffect(() => {
    void refresh()
    function onNet() { setConn(getConnState()) }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onNet)
      window.addEventListener('offline', onNet)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onNet)
        window.removeEventListener('offline', onNet)
      }
    }
  }, [])

  async function handleRetryAll() {
    setBusy('retrying')
    try {
      await retryAll()
      await refresh()
    } finally {
      setBusy('idle')
    }
  }

  async function handleFlush() {
    setBusy('flushing')
    try {
      const { flushed } = await flushQueue()
      setFlushNote(flushed)
      await refresh()
    } finally {
      setBusy('idle')
      setConfirmFlush(false)
    }
  }

  const queueCount = items.length
  const queueCountLabel = (queueCount === 1 ? t('conexion.queueCount.one') : t('conexion.queueCount.other')).replace('{n}', String(queueCount))

  return (
    <div className="dhk8-cx">
      <header className="dhk8-cx__head">
        <div>
          <h1 className="dhk8-cx__title">{t('conexion.title')}</h1>
          <p className="dhk8-cx__sub">{t('conexion.subtitle')}</p>
        </div>
        <button type="button" className="dhk8-cx__btn" onClick={() => void refresh()} aria-label={t('common.retry')}>
          <RefreshCw size={15} aria-hidden /> {t('common.retry')}
        </button>
      </header>

      {/* Estado de conexión */}
      <section className={`dhk8-cx__conn dhk8-cx__conn--${conn.mode}`} aria-label={t('conexion.title')}>
        <span className="dhk8-cx__conn-icon"><StatusIcon mode={conn.mode} /></span>
        <span className="dhk8-cx__conn-main">{statusLabel(conn.mode)}</span>
        <span className="dhk8-cx__conn-sync">
          {t('conexion.lastSync')}: <strong>{formatLastSync(conn.lastSyncAt)}</strong>
        </span>
      </section>

      {flushNote !== null && (
        <div className="dhk8-cx__note" role="status">
          <strong>{t('conexion.flushed')}.</strong>{' '}
          {t('conexion.flushedDetail').replace('{n}', String(flushNote))}
        </div>
      )}

      {/* Cola de operaciones pendientes */}
      <section className="dhk8-cx__queue" aria-label={t('conexion.queueTitle')}>
        <div className="dhk8-cx__queue-head">
          <h2 className="dhk8-cx__queue-title">{t('conexion.queueTitle')}</h2>
          <span className="dhk8-cx__queue-count">{queueCountLabel}</span>
          <span className="dhk8-cx__spacer" />
          <button
            type="button"
            className="dhk8-cx__btn"
            onClick={() => void handleRetryAll()}
            disabled={busy !== 'idle' || queueCount === 0}
          >
            <RefreshCw size={15} aria-hidden /> {t('conexion.retryAll')}
          </button>
          <button
            type="button"
            className="dhk8-cx__btn dhk8-cx__btn--danger"
            onClick={() => setConfirmFlush(true)}
            disabled={busy !== 'idle' || queueCount === 0}
          >
            <Trash2 size={15} aria-hidden /> {t('conexion.flush')}
          </button>
        </div>

        {queueCount === 0 ? (
          <div className="dhk8-cx__empty">
            <span className="dhk8-cx__empty-title">{t('conexion.empty')}</span>
            <span className="dhk8-cx__empty-hint">{t('conexion.emptyHint')}</span>
          </div>
        ) : (
          <ul className="dhk8-cx__list">
            {items.map((it) => (
              <li key={it.id} className={`dhk8-cx__row dhk8-cx__row--${it.status}`}>
                <span className="dhk8-cx__row-kind">
                  <KindIcon kind={it.kind} />
                  <span className="dhk8-cx__row-kind-label">{kindLabel(it.kind)}</span>
                </span>
                <span className="dhk8-cx__row-body">
                  <span className="dhk8-cx__row-label">{it.label}</span>
                  <span className="dhk8-cx__row-meta">
                    <span className="dhk8-cx__row-age">{formatAge(it.queuedAt)}</span>
                    {attemptsLabel(it.attempts) && (
                      <>
                        <span className="dhk8-cx__row-dot" aria-hidden>·</span>
                        <span className="dhk8-cx__row-attempts">{attemptsLabel(it.attempts)}</span>
                      </>
                    )}
                  </span>
                  {it.status === 'error' && it.errorMessage && (
                    <span className="dhk8-cx__row-error">{it.errorMessage}</span>
                  )}
                </span>
                <span className={`dhk8-cx__badge dhk8-cx__badge--${it.status}`}>{statusItemLabel(it.status)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <BottomSheet
        open={confirmFlush}
        onClose={() => { if (busy === 'idle') setConfirmFlush(false) }}
        title={t('conexion.flush')}
        heightFraction={0.42}
        footer={
          <div className="dhk8-cx__sheet-foot">
            <button type="button" className="dhk8-cx__btn" onClick={() => setConfirmFlush(false)} disabled={busy !== 'idle'}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="dhk8-cx__btn dhk8-cx__btn--danger dhk8-cx__btn--solid"
              onClick={() => void handleFlush()}
              disabled={busy !== 'idle'}
            >
              {busy === 'flushing' ? t('common.loading') : t('conexion.flush')}
            </button>
          </div>
        }
      >
        <p className="dhk8-cx__sheet-body">{t('conexion.confirmFlush')}</p>
      </BottomSheet>

      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-cx { display:flex; flex-direction:column; gap:var(--pos-space-5,20px); max-width:920px; }
  .dhk8-cx__head { display:flex; align-items:flex-start; gap:var(--pos-space-3,12px); flex-wrap:wrap; }
  .dhk8-cx__title { margin:0; font-family:var(--font-display,inherit); font-size:var(--pos-text-2xl,24px); font-weight:var(--pos-weight-semibold,600); letter-spacing:var(--pos-tracking-snug,-0.005em); color:var(--pos-fg,#E8EFE9); }
  .dhk8-cx__sub { margin:var(--pos-space-1,4px) 0 0; font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-cx__spacer { flex:1; }

  .dhk8-cx__btn {
    display:inline-flex; align-items:center; gap:var(--pos-space-2,8px);
    padding:var(--pos-space-2,8px) var(--pos-space-3,12px); min-height:var(--pos-touch-target,44px);
    font:inherit; font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500);
    background:var(--pos-surface,#161C18); color:var(--pos-fg-secondary,#B9C5BC);
    border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); cursor:pointer; white-space:nowrap;
    transition:background var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), border-color var(--pos-duration-fast,120ms) var(--pos-ease-out,ease), color var(--pos-duration-fast,120ms) var(--pos-ease-out,ease);
  }
  .dhk8-cx__btn:hover:not(:disabled) { color:var(--pos-fg,#E8EFE9); background:var(--pos-elevated,#1C231E); border-color:var(--pos-tint,#3FB37F); }
  .dhk8-cx__btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-cx__btn--danger:hover:not(:disabled) { color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch, var(--pos-danger,#E5484D) 55%, transparent); background:var(--pos-danger-bg,color-mix(in oklch,var(--pos-danger,#E5484D) 12%,transparent)); }
  .dhk8-cx__btn--solid { background:var(--pos-danger,#E5484D); color:var(--pos-on-tint,#fff); border-color:var(--pos-danger,#E5484D); }
  .dhk8-cx__btn--solid:hover:not(:disabled) { color:var(--pos-on-tint,#fff); background:color-mix(in oklch,var(--pos-danger,#E5484D) 88%,black); border-color:color-mix(in oklch,var(--pos-danger,#E5484D) 88%,black); }

  /* Estado de conexión */
  .dhk8-cx__conn {
    display:flex; align-items:center; gap:var(--pos-space-3,12px); flex-wrap:wrap;
    padding:var(--pos-space-4,16px); border-radius:var(--pos-radius-lg,12px);
    border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:var(--pos-surface,#161C18);
    font-family:var(--font-display,inherit);
  }
  .dhk8-cx__conn-icon { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; flex-shrink:0; border-radius:var(--pos-radius-pill,999px); color:var(--pos-on-tint,#fff); }
  .dhk8-cx__conn--cloud-direct { border-color:color-mix(in oklch,var(--pos-success,#30A46C) 45%,var(--pos-separator-strong,rgba(255,255,255,.14))); }
  .dhk8-cx__conn--cloud-direct .dhk8-cx__conn-icon { background:var(--pos-success,#30A46C); }
  .dhk8-cx__conn--lan-relay { border-color:color-mix(in oklch,var(--pos-info,#0091FF) 45%,var(--pos-separator-strong,rgba(255,255,255,.14))); }
  .dhk8-cx__conn--lan-relay .dhk8-cx__conn-icon { background:var(--pos-info,#0091FF); }
  .dhk8-cx__conn--offline { border-color:color-mix(in oklch,var(--pos-danger,#E5484D) 45%,var(--pos-separator-strong,rgba(255,255,255,.14))); }
  .dhk8-cx__conn--offline .dhk8-cx__conn-icon { background:var(--pos-danger,#E5484D); }
  .dhk8-cx__conn-main { font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-cx__conn-sync { font-size:var(--pos-text-sm,13px); color:var(--pos-fg-tertiary,#7E8C82); margin-left:auto; }
  .dhk8-cx__conn-sync strong { color:var(--pos-fg-secondary,#B9C5BC); font-weight:var(--pos-weight-medium,500); }

  .dhk8-cx__note {
    padding:var(--pos-space-3,12px) var(--pos-space-4,16px); border-radius:var(--pos-radius-md,8px);
    background:var(--pos-success-bg,color-mix(in oklch,var(--pos-success,#30A46C) 14%,transparent));
    border:1px solid color-mix(in oklch,var(--pos-success,#30A46C) 35%,transparent);
    color:var(--pos-fg,#E8EFE9); font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px);
  }
  .dhk8-cx__note strong { color:var(--pos-success,#30A46C); }

  /* Cola */
  .dhk8-cx__queue { display:flex; flex-direction:column; gap:var(--pos-space-3,12px); }
  .dhk8-cx__queue-head { display:flex; align-items:center; gap:var(--pos-space-3,12px); flex-wrap:wrap; }
  .dhk8-cx__queue-title { margin:0; font-family:var(--font-display,inherit); font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-cx__queue-count { font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }

  .dhk8-cx__empty {
    display:flex; flex-direction:column; gap:var(--pos-space-1,4px);
    padding:var(--pos-space-8,32px) var(--pos-space-4,16px); text-align:center;
    border:1px dashed var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-lg,12px);
    font-family:var(--font-display,inherit);
  }
  .dhk8-cx__empty-title { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg-secondary,#B9C5BC); }
  .dhk8-cx__empty-hint { font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }

  .dhk8-cx__list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; border:1px solid var(--pos-separator,rgba(255,255,255,.08)); border-radius:var(--pos-radius-lg,12px); overflow:hidden; }
  .dhk8-cx__row {
    display:flex; align-items:flex-start; gap:var(--pos-space-3,12px);
    padding:var(--pos-space-3,12px) var(--pos-space-4,16px);
    border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.08)); background:var(--pos-surface,#161C18);
    font-family:var(--font-display,inherit);
  }
  .dhk8-cx__row:last-child { border-bottom:none; }
  .dhk8-cx__row--error { background:var(--pos-danger-bg,color-mix(in oklch,var(--pos-danger,#E5484D) 8%,transparent)); }
  .dhk8-cx__row-kind { display:flex; align-items:center; gap:var(--pos-space-2,8px); flex-shrink:0; color:var(--pos-fg-tertiary,#7E8C82); min-width:96px; }
  .dhk8-cx__row-kind-label { font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:var(--pos-tracking-wide,.04em); text-transform:uppercase; }
  .dhk8-cx__row-body { display:flex; flex-direction:column; gap:var(--pos-space-1,4px); flex:1; min-width:0; }
  .dhk8-cx__row-label { font-size:var(--pos-text-sm,13px); color:var(--pos-fg,#E8EFE9); font-variant-numeric:tabular-nums; word-break:break-word; }
  .dhk8-cx__row-meta { display:flex; align-items:center; gap:var(--pos-space-2,8px); font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); flex-wrap:wrap; }
  .dhk8-cx__row-dot { opacity:.6; }
  .dhk8-cx__row-error { font-size:var(--pos-text-xs,12px); color:var(--pos-danger,#E5484D); }
  .dhk8-cx__badge {
    flex-shrink:0; align-self:center; padding:2px var(--pos-space-2,8px); border-radius:var(--pos-radius-pill,999px);
    font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:var(--pos-tracking-wide,.04em); text-transform:uppercase;
    border:1px solid transparent;
  }
  .dhk8-cx__badge--pendiente { color:var(--pos-fg-secondary,#B9C5BC); border-color:var(--pos-separator-strong,rgba(255,255,255,.14)); }
  .dhk8-cx__badge--reintentando { color:var(--pos-info,#0091FF); border-color:color-mix(in oklch,var(--pos-info,#0091FF) 40%,transparent); background:var(--pos-info-bg,color-mix(in oklch,var(--pos-info,#0091FF) 12%,transparent)); }
  .dhk8-cx__badge--error { color:var(--pos-danger,#E5484D); border-color:color-mix(in oklch,var(--pos-danger,#E5484D) 45%,transparent); background:var(--pos-danger-bg,color-mix(in oklch,var(--pos-danger,#E5484D) 12%,transparent)); }

  .dhk8-cx__sheet-body { margin:0; font-family:var(--font-display,inherit); font-size:var(--pos-text-md,15px); color:var(--pos-fg-secondary,#B9C5BC); line-height:var(--pos-leading-normal,1.5); }
  .dhk8-cx__sheet-foot { display:flex; gap:var(--pos-space-3,12px); justify-content:flex-end; }

  @media (max-width:520px) {
    .dhk8-cx__conn-sync { margin-left:0; width:100%; }
    .dhk8-cx__queue-head .dhk8-cx__spacer { display:none; }
    .dhk8-cx__queue-head .dhk8-cx__btn { flex:1; justify-content:center; }
    .dhk8-cx__row { flex-wrap:wrap; }
    .dhk8-cx__row-kind { min-width:0; }
    .dhk8-cx__badge { align-self:flex-start; }
    .dhk8-cx__sheet-foot { flex-direction:column-reverse; }
    .dhk8-cx__sheet-foot .dhk8-cx__btn { width:100%; justify-content:center; }
  }
`
